// SCR-AUTH-001 (بخش Phoenix) — بازیابی رمز (US-IAM-004): موبایل ← OTP ← رمز جدید با نمایش قواعد رمز (BR-IAM-002)؛ پاسخ و پیام
// یکسان برای موبایل موجود و ناموجود (C-AGENT-181)؛ موفقیت ← همه نشست‌ها باطل (BR-IAM-008) و ورود دوباره. پیوند «فراموشی رمز»
// تم Keycloak به این صفحه می‌آید (deploy/keycloak/themes/phoenix).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { unwrap, type OtpChallenge } from '@phoenix/api-client';
import { useAuth, usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, ErrorState, faDigits, MobileFieldRHF, zMobile } from '@phoenix/ui';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { OtpPasswordForm } from './OtpPasswordForm';
import { PublicLayout } from './PublicLayout';

export const SCR_AUTH_001 = 'SCR-AUTH-001';
export const RECOVER_PATH = '/recover';

const mobileSchema = z.object({ mobile: zMobile() });
type MobileForm = z.input<typeof mobileSchema>;

export function RecoverPage() {
  const { t } = useTranslation('iam');
  const api = usePublicApi();
  const auth = useAuth();
  const [mobile, setMobile] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [done, setDone] = useState(false);

  const { control, handleSubmit, setError } = useForm<MobileForm>({
    resolver: zodResolver(mobileSchema),
    defaultValues: { mobile: '' },
  });

  const start = useMutation({
    mutationFn: (value: string) =>
      unwrap(api.POST('/api/v1/identity/password-recovery:start', { body: { mobile: value } })),
    onSuccess: (result, value) => {
      setMobile(value);
      setChallenge(result);
    },
    onError: (error) => {
      applyServerErrors(error, setError, { fields: ['mobile'] });
    },
  });
  const complete = useMutation({
    mutationFn: ({ code, password }: { code: string; password: string }) =>
      unwrap(
        api.POST('/api/v1/identity/password-recovery:complete', {
          body: { otpId: challenge?.otpId ?? '', code, newPassword: password, totpCode: null },
        }),
      ),
    onSuccess: () => setDone(true),
  });

  let body;
  if (done) {
    body = (
      <Stack spacing={2} data-testid="recovery-done">
        <Alert severity="success">
          <AlertTitle>{t('iam:recover.done_title')}</AlertTitle>
          {t('iam:recover.done_description')}
        </Alert>
        <Button variant="contained" onClick={() => void auth.login('/')} data-testid="recovery-login">
          {t('iam:recover.login')}
        </Button>
      </Stack>
    );
  } else if (challenge && mobile) {
    body = (
      <Stack spacing={2}>
        {/* پیام یکسان برای موبایل موجود و ناموجود (شمارش‌ناپذیری حساب). */}
        <Alert severity="info" data-testid="recovery-sent">
          {t('iam:recover.sent', { mobile: faDigits(mobile) })}
        </Alert>
        <OtpPasswordForm
          submitKey="iam:recover.submit"
          mobile={mobile}
          busy={complete.isPending}
          error={complete.error ?? start.error}
          resendBusy={start.isPending}
          onResend={() => {
            complete.reset();
            start.mutate(mobile);
          }}
          onSubmit={(values) => complete.mutate(values)}
        />
      </Stack>
    );
  } else {
    body = (
      <Box
        component="form"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void handleSubmit((form) => start.mutate(form.mobile))();
        }}
      >
        <Stack spacing={2}>
          <MobileFieldRHF control={control} name="mobile" labelKey="iam:recover.mobile" required autoFocus />
          {start.error ? <ErrorState error={start.error} compact /> : null}
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" disabled={start.isPending} data-testid="recovery-start">
              {t('iam:otp.send')}
            </Button>
            <Button onClick={() => void auth.login('/')}>{t('iam:recover.back_to_login')}</Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <PublicLayout screenId={SCR_AUTH_001} titleKey="iam:recover.title" subtitle={t('iam:recover.subtitle')}>
      {body}
    </PublicLayout>
  );
}
